/**
 * Centralized WebSocket Management Service
 * Handles all WebSocket connections to prevent conflicts and improve stability
 */

import WebSocketKeepalive from './WebSocketKeepalive';

class WebSocketManager {
  constructor() {
    this.connections = new Map();
    this.subscribers = new Map();
    this.messageQueue = new Map();
    // Keep persistent reconnect attempts for long-lived admin chat
    this.maxRetries = 1000;
    this.retryDelay = 5000;
    
    // Background message buffering - stores messages received while in background
    // for batch processing when returning to foreground
    this.backgroundMessageBuffer = new Map(); // connectionKey -> { messages: [], lastVisibleTime: number }
    this._isTabVisible = typeof document !== 'undefined' ? document.visibilityState === 'visible' : true;
    this._lastVisibleTime = Date.now();
    this._lastHiddenTime = null;
    
    // Initialize cleanup interval with safety check
    this.cleanupInterval = setInterval(() => {
      if (typeof this.performPeriodicCleanup === 'function') {
        this.performPeriodicCleanup();
      } else {
        console.warn('[WebSocketManager] performPeriodicCleanup method not available yet');
      }
    }, 300000); // 5 minutes

    // Gracefully handle page lifecycle without aggressively closing sockets on background
    if (typeof window !== 'undefined') {
      // Only cleanup on hard unloads
      this._handleBeforeUnload = () => {
        try {
          this.cleanup();
        } catch (e) {
          // noop
        }
      };
      window.addEventListener('beforeunload', this._handleBeforeUnload, { once: true });

      // When tab becomes visible again, ensure connections are re-opened promptly
      this._handleVisibilityChange = () => {
        try {
          if (document.visibilityState === 'hidden') {
            // Tab going to background
            this._isTabVisible = false;
            this._lastHiddenTime = Date.now();
            console.log('[WebSocketManager] 📱 Tab going to background');
          } else if (document.visibilityState === 'visible') {
            const timeInBackground = this._lastHiddenTime ? Date.now() - this._lastHiddenTime : 0;
            this._isTabVisible = true;
            this._lastVisibleTime = Date.now();
            
            console.log(`[WebSocketManager] 👁️ Tab became visible (was in background for ${Math.round(timeInBackground/1000)}s), checking connections...`);
            
            // Process any buffered background messages first
            this._processBackgroundMessageBuffer();
            
            // Iterate existing connections and reconnect closed or potentially stale ones
            this.connections.forEach((connection, connectionKey) => {
              if (!connection) return;
              const ws = connection.ws;
              const state = ws ? ws.readyState : WebSocket.CLOSED;
              
              // Update visibility timestamp for keepalive grace period
              if (connection.keepalive) {
                connection.keepalive._lastVisibleTime = Date.now();
                connection.keepalive.lastPingReceived = Date.now(); // Give grace period
              }
              
              // Calculate time since last REAL activity (not ping/pong) - if too long, connection is likely stale
              // Use lastNonPingActivity if available, fallback to lastActivity
              const lastRealActivity = connection.lastNonPingActivity || connection.lastActivity || 0;
              const timeSinceLastRealActivity = Date.now() - lastRealActivity;
              
              // Be more tolerant after returning from background - use 3 minutes instead of 2
              // This prevents false positives when tab was suspended by browser
              const staleThreshold = timeInBackground > 60000 ? 180000 : 120000;
              const isLikelyStale = timeSinceLastRealActivity > staleThreshold;
              
              if (state !== WebSocket.OPEN) {
                console.log(`[WebSocketManager] Connection ${connectionKey.substring(0, 50)}... is closed, reconnecting`);
                if (connection.keepalive) {
                  connection.keepalive.forceReconnect();
                } else {
                  // Fallback: recreate connection using stored endpoint/token
                  const endpoint = connection.endpoint;
                  try {
                    const url = new URL(connectionKey);
                    const token = url.searchParams.get('token');
                    this.getConnection(endpoint, token).catch(() => {});
                  } catch (e) {
                    // If parsing fails, skip
                  }
                }
              } else if (isLikelyStale) {
                // Connection appears OPEN but has been idle too long - likely zombie
                console.warn(`[WebSocketManager] Connection stale (${Math.round(timeSinceLastRealActivity/1000)}s since last real activity), forcing reconnect`);
                if (connection.keepalive) {
                  connection.keepalive.forceReconnect();
                }
              } else {
                // Connection appears OPEN and recent - verify with a test ping
                console.log(`[WebSocketManager] Connection appears OPEN, sending test ping to verify`);
                try {
                  ws.send(JSON.stringify({ type: 'pong', timestamp: Date.now(), visibility_check: true }));
                  connection.lastActivity = Date.now();
                } catch (e) {
                  // Failed to send - connection is actually dead
                  console.warn(`[WebSocketManager] Connection is zombie (send failed), forcing reconnect`);
                  if (connection.keepalive) {
                    connection.keepalive.forceReconnect();
                  }
                }
              }
            });
            
            // Reset hidden time after processing
            this._lastHiddenTime = null;
          }
        } catch (e) {
          console.error('[WebSocketManager] Error in visibility change handler:', e);
        }
      };
      document.addEventListener('visibilitychange', this._handleVisibilityChange);
      
      // iOS PWA specific: Handle pageshow event for Back-Forward Cache restoration
      // This is CRITICAL for iOS Safari PWA where visibilitychange might not fire
      this._handlePageShow = (event) => {
        // event.persisted = true means page was restored from BFCache (common on iOS PWA)
        if (event.persisted) {
          console.log('[WebSocketManager] 📱 Page restored from BFCache (iOS PWA), checking connections...');
          this._handleVisibilityChange(); // Reuse visibility logic
        }
      };
      window.addEventListener('pageshow', this._handlePageShow);
      
      // Handle focus event as additional trigger for PWA (some browsers don't fire visibilitychange)
      this._handleFocus = () => {
        // Detect if running in PWA mode
        const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                      window.navigator.standalone === true;
        
        if (isPWA) {
          console.log('[WebSocketManager] 📱 PWA window focused, verifying connections...');
          // Small delay to allow visibility event to fire first
          setTimeout(() => {
            this.connections.forEach((connection, connectionKey) => {
              if (!connection) return;
              const ws = connection.ws;
              const state = ws ? ws.readyState : WebSocket.CLOSED;
              
              if (state !== WebSocket.OPEN) {
                console.log(`[WebSocketManager] PWA focus: Connection closed, reconnecting`);
                if (connection.keepalive) {
                  connection.keepalive.forceReconnect();
                }
              }
            });
          }, 500);
        }
      };
      window.addEventListener('focus', this._handleFocus);
      
      // Handle online/offline events for network-aware reconnection
      this._isOnline = navigator.onLine !== false; // Assume online if unknown
      
      this._handleOnline = () => {
        console.log('[WebSocketManager] 🌐 Network online, attempting to restore connections...');
        this._isOnline = true;
        
        // Delay slightly to allow network to stabilize
        setTimeout(() => {
          this.connections.forEach((connection, connectionKey) => {
            if (!connection) return;
            const ws = connection.ws;
            const state = ws ? ws.readyState : WebSocket.CLOSED;
            
            if (state !== WebSocket.OPEN && connection.keepalive) {
              console.log(`[WebSocketManager] Reconnecting after network restore: ${connectionKey.substring(0, 50)}...`);
              connection.keepalive.reconnectAttempts = 0; // Reset attempts on network change
              connection.keepalive.forceReconnect();
            }
          });
        }, 1000);
      };
      
      this._handleOffline = () => {
        console.log('[WebSocketManager] 📴 Network offline, pausing reconnection attempts');
        this._isOnline = false;
        // Don't close connections - they might recover when network returns
      };
      
      window.addEventListener('online', this._handleOnline);
      window.addEventListener('offline', this._handleOffline);
    }
  }

  /**
   * Periodic cleanup of stale connections
   */
  performPeriodicCleanup() {
    const now = Date.now();
    const staleThreshold = 4 * 60 * 60 * 1000; // 4 hours for general cleanup
    const activityStaleThreshold = 3 * 60 * 1000; // 3 minutes for real activity check (more tolerant for periodic check)

    console.log('[WebSocketManager] Performing periodic cleanup');

    for (const [connectionKey, connection] of this.connections.entries()) {
      if (!connection) continue;
      const lastActivity = connection.lastActivity || 0;
      const lastNonPingActivity = connection.lastNonPingActivity || 0;
      const isStale = (now - lastActivity) > staleThreshold;
      const isActivityStale = (now - lastNonPingActivity) > activityStaleThreshold;
      const readyState = connection.ws ? connection.ws.readyState : WebSocket.CLOSED;
      const isDead = readyState === WebSocket.CLOSED;
      const subscriberSet = this.subscribers.get(connectionKey);
      const hasNoSubscribers = !subscriberSet || subscriberSet.size === 0;

      // If nobody is listening, clean up aggressively.
      if (hasNoSubscribers) {
        console.log(`[WebSocketManager] Cleaning up unused connection: ${connectionKey}`);
        this.cleanupConnection(connectionKey);
        continue;
      }

      // If the socket is closed, cleanup to allow a fresh reconnect by subscribers.
      if (isDead) {
        console.log(`[WebSocketManager] Cleaning up closed connection: ${connectionKey}`);
        this.cleanupConnection(connectionKey);
        continue;
      }

      // If connection appears OPEN but has no real activity for a while, force reconnect
      // This catches "zombie" connections that only have ping/pong but no real messages
      if (readyState === WebSocket.OPEN && isActivityStale && document.visibilityState === 'visible') {
        console.warn(`[WebSocketManager] Connection has no real activity for ${Math.round((now - lastNonPingActivity)/1000)}s, forcing reconnect: ${connectionKey.substring(0, 50)}...`);
        if (connection.keepalive && typeof connection.keepalive.forceReconnect === 'function') {
          connection.keepalive.forceReconnect();
          connection.lastActivity = now;
          connection.lastNonPingActivity = now;
        }
        continue;
      }

      // If the connection looks stale but still has subscribers, prefer a gentle reconnect
      // instead of wiping the connection (which can strand subscribers until manual refresh).
      if (isStale) {
        console.warn(`[WebSocketManager] Stale connection detected, forcing reconnect: ${connectionKey}`);
        if (connection.keepalive && typeof connection.keepalive.forceReconnect === 'function') {
          connection.keepalive.forceReconnect();
          connection.lastActivity = now;
        } else {
          // Fallback: attempt to close and recreate via keepalive-less path
          try {
            if (connection.ws && connection.ws.readyState === WebSocket.OPEN) {
              connection.ws.close(1000, 'Stale connection - reconnect');
            }
          } catch (e) {
            // noop
          }
        }
      }
    }
  }

  /**
   * Get or create a WebSocket connection for an endpoint
   */
  async getConnection(endpoint, token) {
    const { fullUrl, connectionKey } = this._buildUrlAndKey(endpoint, token);
    
    console.log(`[WebSocketManager] Getting connection for: ${endpoint} (Total connections: ${this.connections.size})`);
    
    // Log all existing connections for debugging
    if (this.connections.size > 0) {
      console.log(`[WebSocketManager] Existing connections:`, 
        Array.from(this.connections.keys()).map(key => ({
          key: key.substring(0, 50) + '...', 
          status: this.connections.get(key).ws.readyState,
          subscribers: this.subscribers.get(key)?.size || 0
        }))
      );
    }
    
    if (this.connections.has(connectionKey)) {
      const conn = this.connections.get(connectionKey);
      const readyState = conn.ws.readyState;
      
      console.log(`[WebSocketManager] Found existing connection with state: ${readyState} (${
        readyState === WebSocket.CONNECTING ? 'CONNECTING' :
        readyState === WebSocket.OPEN ? 'OPEN' :
        readyState === WebSocket.CLOSING ? 'CLOSING' :
        readyState === WebSocket.CLOSED ? 'CLOSED' : 'UNKNOWN'
      })`);
      
      if (readyState === WebSocket.OPEN) {
        console.log(`[WebSocketManager] Using existing OPEN connection: ${endpoint}`);
        return conn;
      } else if (readyState === WebSocket.CONNECTING) {
        console.log(`[WebSocketManager] Connection still connecting, waiting...`);
        // Wait for the connection to complete
        return new Promise((resolve, reject) => {
          const originalOnOpen = conn.ws.onopen;
          const originalOnError = conn.ws.onerror;
          
          conn.ws.onopen = (event) => {
            if (originalOnOpen) originalOnOpen.call(conn.ws, event);
            resolve(conn);
          };
          
          conn.ws.onerror = (event) => {
            if (originalOnError) originalOnError.call(conn.ws, event);
            reject(event);
          };
        });
      } else {
        console.log(`[WebSocketManager] Cleaning up connection in state: ${readyState}`);
        // Clean up dead/closing connection
        this.cleanupConnection(connectionKey);
      }
    }

    console.log(`[WebSocketManager] Creating new connection for: ${endpoint}`);
    return this.createConnection(endpoint, token);
  }

  /**
   * Create a new WebSocket connection
   */
  createConnection(endpoint, token) {
    const { fullUrl, connectionKey } = this._buildUrlAndKey(endpoint, token);

    console.log(`[WebSocketManager] Creating connection to: ${endpoint}`);

    return new Promise((resolve, reject) => {
      const ws = new WebSocket(fullUrl);
      const subscribers = new Set();
      
      const connection = {
        ws,
        keepalive: null,
        subscribers,
        endpoint,
        isReady: false,
        lastActivity: Date.now(),
        lastNonPingActivity: Date.now(),
        createdAt: Date.now(),
        closeCount: 0,
        lastCloseTime: null,
        railwayReconnectTimer: null // Add Railway-specific timer
      };

      // Store connection early for access in event handlers
      this.connections.set(connectionKey, connection);
      this.subscribers.set(connectionKey, subscribers);
      this.messageQueue.set(connectionKey, []);

      // Unified event handler setup
      this.setupWebSocketEvents(ws, connectionKey, resolve, reject);

      // Create keepalive instance with optimized handlers
      connection.keepalive = new WebSocketKeepalive(ws, {
        autoReconnect: true,
        reconnectInterval: 10000,
        maxReconnectAttempts: this.maxRetries,
        debug: process.env.NODE_ENV === 'development',
        managedMode: true, // Let WebSocketManager handle ping/pong
        onReconnect: (newWebSocket) => this.handleReconnection(newWebSocket, connectionKey),
        onMaxReconnectAttemptsReached: () => this.handleConnectionFailure(connectionKey, endpoint)
      });

      // Railway-specific: Proactive reconnection before 75-second timeout
      // this.setupRailwayProactiveReconnect(connection, connectionKey);
    });
  }

  /**
   * Unified WebSocket event handler setup
   */
  setupWebSocketEvents(ws, connectionKey, resolve = null, reject = null) {
    const connection = this.connections.get(connectionKey);
    if (!connection) return;

    const handleOpened = () => {
      console.log(`[WebSocketManager] Connection opened: ${connectionKey}`);
      connection.isReady = true;
      connection.lastActivity = Date.now();
      // Debounce connection_opened until socket is stably open
      if (connection._openedNotified) return;
      const startCloseCount = connection.closeCount || 0;
      setTimeout(() => {
        const stillOpen = ws.readyState === WebSocket.OPEN;
        const notClosed = (connection.closeCount || 0) === startCloseCount;
        if (stillOpen && notClosed && !connection._openedNotified) {
          connection._openedNotified = true;
          this.processQueuedMessages(connectionKey);
          this.notifySubscribers(connectionKey, {
            type: 'connection_opened',
            connectionKey
          });
          if (resolve) resolve(connection);
        }
      }, 300);
    };

    ws.onopen = () => {
      handleOpened();
    };

    // If the socket is already open (possible during keepalive reconnection), run open handler immediately
    if (ws.readyState === WebSocket.OPEN) {
      handleOpened();
    }

    ws.onmessage = (event) => {
      const now = Date.now();
      connection.lastActivity = now;
      try {
        const data = JSON.parse(event.data);
        if (data && data.type !== 'ping' && data.type !== 'pong') {
          connection.lastNonPingActivity = now;
        }
        
        // Handle APPLICATION-LEVEL ping/pong (current setup)
        // Note: If using Daphne ping/pong instead, this code block would be removed
        // because Daphne ping/pong is handled automatically at the WebSocket transport level
        if (data.type === 'ping') {
          // Update keepalive timer too, even in managed mode
          if (connection.keepalive) {
            connection.keepalive.lastPingReceived = Date.now();
          }
          // If we haven't notified open yet, do it now on first ping (stable)
          if (!connection._openedNotified) {
            connection._openedNotified = true;
            this.processQueuedMessages(connectionKey);
            this.notifySubscribers(connectionKey, {
              type: 'connection_opened',
              connectionKey
            });
            if (resolve) resolve(connection);
          }
          console.log(`[WebSocketManager] Received ping, responding with pong for: ${connectionKey}`);
          // Respond with pong immediately
          try {
            ws.send(JSON.stringify({ type: 'pong', timestamp: Date.now() }));
            console.log(`[WebSocketManager] Pong sent successfully for: ${connectionKey}`);
          } catch (pongError) {
            console.error(`[WebSocketManager] Failed to send pong:`, pongError);
            // If we can't send pong, the connection is likely broken
            if (connection && connection.keepalive) {
              console.log(`[WebSocketManager] Triggering reconnection due to pong failure`);
              connection.keepalive.forceReconnect();
            }
          }
          // Don't notify subscribers of ping messages - handle internally
          return;
        }
        
        this.notifySubscribers(connectionKey, data);
      } catch (error) {
        console.error(`[WebSocketManager] Error parsing message:`, error);
      }
    };

    ws.onclose = (event) => {
      console.log(`[WebSocketManager] Connection closed: ${connectionKey}`, {
        code: event.code,
        reason: event.reason || 'No reason provided',
        wasClean: event.wasClean,
        timestamp: new Date().toISOString(),
        subscriberCount: this.subscribers.get(connectionKey)?.size || 0
      });
      
      // Log specific close codes for debugging
      const closeReasons = {
        1000: 'Normal Closure (Backend initiated)',
        1001: 'Going Away',
        1002: 'Protocol Error', 
        1003: 'Unsupported Data',
        1006: 'Abnormal Closure',
        1011: 'Server Error',
        3001: 'Ping Timeout',
        4001: 'Authentication Failed',
        4002: 'Invalid Token',
        4003: 'Token Expired',
        4500: 'Custom Backend Error (possible auth/protocol issue)'
      };
      
      const closeReason = closeReasons[event.code] || `Unknown (${event.code})`;
      console.log(`[WebSocketManager] Close reason: ${closeReason}`);

      // If auth issues suspected, immediately refresh token and force reconnect
      if ([4001, 4002, 4003, 4401, 4500].includes(event.code)) {
        try {
          const url = new URL(connectionKey);
          const prevToken = url.searchParams.get('token');
          const latestToken = localStorage.getItem('token');
          if (latestToken && latestToken !== prevToken) {
            console.log('[WebSocketManager] Detected token refresh, forcing immediate reconnect with new token');
            const endpoint = connection.endpoint;
            // Save subscribers BEFORE cleanup
            const oldSubscribers = this.subscribers.get(connectionKey);
            const subscribersList = oldSubscribers ? Array.from(oldSubscribers) : [];
            // Clean up the stale connection entry
            this.cleanupConnection(connectionKey);
            // Create a new connection and reattach subscribers
            this.getConnection(endpoint, latestToken).then(() => {
              const { connectionKey: newKey } = this._buildUrlAndKey(endpoint, latestToken);
              const newSubs = this.subscribers.get(newKey) || new Set();
              subscribersList.forEach((s) => newSubs.add(s));
              this.subscribers.set(newKey, newSubs);
            }).catch(() => {});
          } else if (event.code === 4500) {
            // PWA/proxy edge-case: force cache-busting reconnect even if token unchanged
            try {
              const endpoint = connection.endpoint;
              const bustUrl = new URL(endpoint, typeof window !== 'undefined' ? window.location.href : undefined);
              bustUrl.searchParams.set('r', Date.now().toString());
              const bustedEndpoint = bustUrl.toString();
              const oldSubscribers = this.subscribers.get(connectionKey);
              const subscribersList = oldSubscribers ? Array.from(oldSubscribers) : [];
              this.cleanupConnection(connectionKey);
              this.getConnection(bustedEndpoint, latestToken || prevToken).then(() => {
                const { connectionKey: newKey } = this._buildUrlAndKey(bustedEndpoint, latestToken || prevToken);
                const newSubs = this.subscribers.get(newKey) || new Set();
                subscribersList.forEach((s) => newSubs.add(s));
                this.subscribers.set(newKey, newSubs);
              }).catch(() => {});
            } catch (e2) {
              // ignore
            }
          }
        } catch (e) {
          // noop
        }
      }
      
      // Detect if running in PWA mode
      const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                    window.navigator.standalone === true ||
                    document.referrer.includes('android-app://');
      
      // Special handling for error code 4500 in PWA
      if (event.code === 4500) {
        console.error(`[WebSocketManager] ⚠️ Error 4500 detected${isPWA ? ' in PWA mode' : ''}. This may indicate:
          - Backend authentication issue
          - Token format/validation problem
          - WebSocket protocol mismatch
          ${isPWA ? '- PWA Service Worker interference' : ''}
          - CORS or security policy issue`);
        
        if (isPWA) {
          console.warn(`[WebSocketManager] 🔧 PWA-specific debugging:
            - Check if Service Worker is intercepting WebSocket connections
            - Verify token is being sent correctly in PWA context
            - Check if PWA manifest or Service Worker needs WebSocket permissions`);
        }
      }
      
      // Track connection close frequency
      if (connection) {
        connection.closeCount++;
        connection.lastCloseTime = Date.now();
        const connectionLifetime = Date.now() - connection.createdAt;
        const avgLifetime = connectionLifetime / connection.closeCount;
        
        console.log(`[WebSocketManager] Connection stats: lifetime=${(connectionLifetime/1000).toFixed(1)}s, closes=${connection.closeCount}, avg=${(avgLifetime/1000).toFixed(1)}s`);
        
        // Special handling for backend-initiated normal closures
        if (event.code === 1000) {
          if (connection.closeCount > 3 && avgLifetime < 30000) { // Less than 30s average
            console.error(`[WebSocketManager] 🚨 FREQUENT BACKEND CLOSURES DETECTED!
            - Close count: ${connection.closeCount}
            - Average lifetime: ${(avgLifetime/1000).toFixed(1)}s
            - This suggests a backend issue that needs investigation`);
            
            // Notify subscribers of the backend issue
            this.notifySubscribers(connectionKey, {
              type: 'backend_issue_detected',
              issue: 'frequent_closures',
              closeCount: connection.closeCount,
              avgLifetime: avgLifetime,
              suggestion: 'Backend may be experiencing issues. Auto-reconnection will continue with longer delays.'
            });
          } else {
            console.warn(`[WebSocketManager] ⚠️ Backend closed connection normally - this might indicate:
            1. Token authentication issues
            2. Backend connection management logic  
            3. Resource limits or cleanup procedures
            4. Multiple connection detection`);
          }
        }
      }
      
      connection.isReady = false;
      connection._openedNotified = false; // Reset so reconnection can notify subscribers again
      this.notifySubscribers(connectionKey, {
        type: 'connection_closed',
        connectionKey,
        code: event.code,
        reason: event.reason,
        closeReason
      });

      // If no subscribers remain for this connection, clean it up immediately
      const remaining = this.subscribers.get(connectionKey)?.size || 0;
      if (remaining === 0) {
        this.cleanupConnection(connectionKey);
      } else if (connection.keepalive && !connection.keepalive.isDestroyed) {
        // CRITICAL: Trigger keepalive reconnection if there are still subscribers
        // This is needed because setupWebSocketEvents overwrites keepalive's onclose handler
        console.log(`[WebSocketManager] Triggering keepalive reconnection for ${connectionKey}`);
        connection.keepalive.lastCloseEvent = event;
        connection.keepalive.scheduleReconnect();
      }
    };

    ws.onerror = (error) => {
      // Detect if running in PWA mode
      const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                    window.navigator.standalone === true ||
                    document.referrer.includes('android-app://');
      
      console.error(`[WebSocketManager] Connection error${isPWA ? ' (PWA mode)' : ''}: ${connectionKey}`, {
        error,
        readyState: ws.readyState,
        url: ws.url,
        isPWA,
        timestamp: new Date().toISOString()
      });
      
      if (connection && isPWA) {
        console.warn(`[WebSocketManager] 🔧 PWA-specific error context:
          - Connection age: ${Date.now() - connection.createdAt}ms
          - Close count: ${connection.closeCount}
          - Token present: ${ws.url.includes('token=')}
          - User agent: ${navigator.userAgent.substring(0, 100)}...
          
          Common PWA WebSocket issues:
          - Service Worker interference (check if SW is registered)
          - Token storage/retrieval in PWA context
          - PWA network policies or manifest configuration
          - Cache-first strategy blocking WebSocket upgrades`);
      }
      
      this.notifySubscribers(connectionKey, {
        type: 'connection_error',
        connectionKey,
        error,
        isPWA
      });
      if (reject) reject(error);
    };
  }

  /**
   * Handle reconnection events
   */
  handleReconnection(newWebSocket, connectionKey) {
    console.log(`[WebSocketManager] Reconnected: ${connectionKey}`);
    const connection = this.connections.get(connectionKey);
    if (!connection) return;

    connection.ws = newWebSocket;
    connection.lastActivity = Date.now();
    connection.createdAt = Date.now(); // Reset creation time for Railway timer
    
    // CRITICAL: Reset connection state flags so connection_opened fires again
    connection.isReady = false;
    connection._openedNotified = false;
    
    // CRITICAL: Reset keepalive reconnection counter on successful reconnect
    // This is needed because setupWebSocketEvents overwrites keepalive's onopen handler
    // which normally resets reconnectAttempts
    if (connection.keepalive) {
      connection.keepalive.reconnectAttempts = 0;
      connection.keepalive.lastPingReceived = Date.now();
    }

    // Set up event handlers for the new WebSocket
    this.setupWebSocketEvents(newWebSocket, connectionKey);
    // Let ws.onopen set isReady, notify subscribers, and flush queue
  }

  /**
   * Railway-specific: Setup proactive reconnection before 75-second timeout
   */
  /*
  setupRailwayProactiveReconnect(connection, connectionKey) {
    // Allow disabling Railway proactive reconnect via environment variable
    if (process.env.REACT_APP_DISABLE_RAILWAY_RECONNECT === 'true') {
      console.log('[WebSocketManager] Railway proactive reconnect disabled via env var');
      return;
    }

    // Clear existing timer
    if (connection.railwayReconnectTimer) {
      clearTimeout(connection.railwayReconnectTimer);
    }

    // Reconnect after 70 seconds (before Railway's ~75 second timeout)
    connection.railwayReconnectTimer = setTimeout(() => {
      const currentConnection = this.connections.get(connectionKey);
      if (currentConnection && currentConnection.ws.readyState === WebSocket.OPEN) {
        console.log(`[WebSocketManager] 🔄 Railway proactive reconnect for: ${connectionKey}`);
        
        // Notify subscribers this is a planned reconnection
        this.notifySubscribers(connectionKey, {
          type: 'railway_proactive_reconnect',
          connectionKey,
          reason: 'Preventing Railway 75-second timeout'
        });

        // Force reconnection via keepalive
        if (currentConnection.keepalive) {
          currentConnection.keepalive.forceReconnect();
        }
      }
    }, 70000); // 70 seconds - before Railway's timeout
  }
  */

  /**
   * Handle connection failure
   */
  handleConnectionFailure(connectionKey, endpoint) {
    console.warn(`[WebSocketManager] Max reconnection attempts reached for: ${endpoint}`);
    this.notifySubscribers(connectionKey, {
      type: 'connection_failed',
      endpoint
    });
  }



  /**
   * Subscribe a component to WebSocket messages
   */
  subscribe(endpoint, token, componentId, messageHandler) {
    console.log(`[WebSocketManager] Subscribing ${componentId} to ${endpoint}`);
    
    const { connectionKey } = this._buildUrlAndKey(endpoint, token);
    console.log(`[WebSocketManager] 🔑 Subscription connectionKey for ${componentId}: ${connectionKey.substring(0, 80)}...`);
    
    // CRITICAL: Clean up stale connections with same endpoint but different token
    // This prevents duplicate backend consumers when token is refreshed
    this._cleanupStaleConnectionsForEndpoint(endpoint, connectionKey);
    
    return this.getConnection(endpoint, token).then(connection => {
      const subscribers = this.subscribers.get(connectionKey);
      
      if (subscribers) {
        // CRITICAL: Check for duplicate subscriptions INSIDE the promise to avoid race conditions
        // This handles React StrictMode double-mounting and rapid re-renders
        const existingSubscriber = Array.from(subscribers).find(sub => sub.componentId === componentId);
        if (existingSubscriber) {
          console.log(`[WebSocketManager] Component ${componentId} already subscribed, replacing handler`);
          subscribers.delete(existingSubscriber);
        }
        
        subscribers.add({ componentId, handler: messageHandler });
        console.log(`[WebSocketManager] Successfully subscribed ${componentId} to ${endpoint} (${subscribers.size} total subscribers)`);
        
        // If connection is already open, immediately notify the new subscriber
        if (connection.ws.readyState === WebSocket.OPEN && connection.isReady) {
          console.log(`[WebSocketManager] Connection already open, immediately notifying ${componentId}`);
          setTimeout(() => {
            messageHandler({
              type: 'connection_opened',
              connectionKey
            });
          }, 0);
        }
        
        // Return unsubscribe function
        return () => this.unsubscribe(endpoint, token, componentId);
      } else {
        console.error(`[WebSocketManager] No subscribers found for ${connectionKey}`);
      }
    }).catch(error => {
      console.error(`[WebSocketManager] Failed to subscribe ${componentId}:`, error);
    });
  }

  /**
   * Unsubscribe a component from WebSocket messages
   */
  unsubscribe(endpoint, token, componentId) {
    const { connectionKey } = this._buildUrlAndKey(endpoint, token);
    const subscribers = this.subscribers.get(connectionKey);
    
    if (subscribers) {
      const toRemove = Array.from(subscribers).find(sub => sub.componentId === componentId);
      if (toRemove) {
        subscribers.delete(toRemove);
        console.log(`[WebSocketManager] Unsubscribed ${componentId} from ${endpoint} (${subscribers.size} remaining)`);
        
        // Clean up connection if no more subscribers - but be defensive about it
        if (subscribers.size === 0) {
          console.log(`[WebSocketManager] No subscribers remaining for ${connectionKey}, scheduling cleanup in 3000ms...`);
          setTimeout(() => {
            // Double-check that there are still no subscribers and connection isn't being used
            const currentSubscribers = this.subscribers.get(connectionKey);
            if (currentSubscribers && currentSubscribers.size === 0) {
              console.log(`[WebSocketManager] Proceeding with cleanup for ${connectionKey}`);
              this.cleanupConnection(connectionKey);
            } else {
              console.log(`[WebSocketManager] Skipping cleanup - new subscribers joined: ${currentSubscribers ? currentSubscribers.size : 0}`);
            }
          }, 3000);
        }
      }
    }
  }

  /**
   * Send a message through WebSocket
   */
  sendMessage(endpoint, token, message) {
    const { connectionKey } = this._buildUrlAndKey(endpoint, token);
    const connection = this.connections.get(connectionKey);
    
    if (connection?.isReady && connection.ws.readyState === WebSocket.OPEN) {
      try {
        const messageStr = JSON.stringify(message);
        connection.ws.send(messageStr);
        connection.lastActivity = Date.now();
        return true;
      } catch (error) {
        console.error(`[WebSocketManager] Error sending message:`, error);
        this.queueMessage(connectionKey, message);
        return false;
      }
    } else {
      // Queue message for later
      this.queueMessage(connectionKey, message);
      return false;
    }
  }

  /**
   * Queue a message for later sending
   */
  queueMessage(connectionKey, message) {
    if (!this.messageQueue.has(connectionKey)) {
      this.messageQueue.set(connectionKey, []);
    }
    
    const queue = this.messageQueue.get(connectionKey);
    
    // Prevent duplicate messages with the same action from being queued
    // This is especially important for sync validation requests
    if (message.action) {
      const existingIndex = queue.findIndex(qMsg => qMsg.action === message.action);
      if (existingIndex !== -1) {
        console.log(`[WebSocketManager] Replacing duplicate ${message.action} message in queue for ${connectionKey}`);
        queue[existingIndex] = message; // Replace with newer version
        return;
      }
    }
    
    queue.push(message);
    
    // Reduced queue size limit to prevent memory issues and excessive retries
    const MAX_QUEUE_SIZE = 20;
    if (queue.length > MAX_QUEUE_SIZE) {
      queue.shift(); // Remove oldest message
      console.warn(`[WebSocketManager] Queue overflow (>${MAX_QUEUE_SIZE}), dropping oldest message for ${connectionKey}`);
    }
    
    console.log(`[WebSocketManager] Queued message (${queue.length} total) for ${connectionKey}`);
  }

  /**
   * Process queued messages
   */
  processQueuedMessages(connectionKey) {
    const queue = this.messageQueue.get(connectionKey);
    const connection = this.connections.get(connectionKey);
    
    if (!queue || queue.length === 0 || !connection?.isReady) return;
    
    console.log(`[WebSocketManager] Processing ${queue.length} queued messages for ${connectionKey}`);
    
    // Process in batches to avoid overwhelming the connection
    const batchSize = 10;
    let processed = 0;
    
    const processBatch = () => {
      for (let i = 0; i < batchSize && queue.length > 0; i++) {
        const message = queue.shift();
        try {
          connection.ws.send(JSON.stringify(message));
          processed++;
        } catch (error) {
          console.error(`[WebSocketManager] Error sending queued message:`, error);
          // Re-queue failed message at the front
          queue.unshift(message);
          break;
        }
      }
      
      // Continue processing if there are more messages
      if (queue.length > 0 && connection.ws.readyState === WebSocket.OPEN) {
        setTimeout(processBatch, 10); // Small delay between batches
      }
    };
    
    processBatch();
  }

  /**
   * Process buffered messages from background period
   * Called when tab becomes visible again
   */
  _processBackgroundMessageBuffer() {
    let totalBuffered = 0;
    
    this.backgroundMessageBuffer.forEach((buffer, connectionKey) => {
      if (buffer.messages && buffer.messages.length > 0) {
        totalBuffered += buffer.messages.length;
        console.log(`[WebSocketManager] 📬 Processing ${buffer.messages.length} buffered messages for ${connectionKey.substring(0, 50)}...`);
        
        // Process buffered messages - deduplicate by message ID if available
        const processedIds = new Set();
        const uniqueMessages = buffer.messages.filter(msg => {
          const msgId = msg.data?.id || msg.data?.message_id;
          if (msgId && processedIds.has(msgId)) {
            return false;
          }
          if (msgId) processedIds.add(msgId);
          return true;
        });
        
        // Notify subscribers of each buffered message
        uniqueMessages.forEach(msg => {
          this._notifySubscribersImmediate(connectionKey, msg);
        });
        
        // Clear the buffer
        buffer.messages = [];
      }
    });
    
    if (totalBuffered > 0) {
      console.log(`[WebSocketManager] ✅ Processed ${totalBuffered} total buffered messages from background`);
    }
  }

  /**
   * Internal method to notify subscribers immediately (used for buffered messages)
   */
  _notifySubscribersImmediate(connectionKey, data) {
    const subscribers = this.subscribers.get(connectionKey);
    if (!subscribers || subscribers.size === 0) return;
    
    const failedSubscribers = [];
    subscribers.forEach((subscriber) => {
      try {
        subscriber.handler(data);
      } catch (error) {
        console.error(`[WebSocketManager] Error in subscriber handler for ${subscriber.componentId}:`, error);
        failedSubscribers.push(subscriber);
      }
    });
    
    failedSubscribers.forEach((subscriber) => {
      subscribers.delete(subscriber);
    });
  }

  /**
   * Notify all subscribers of a message
   */
  notifySubscribers(connectionKey, data) {
    const subscribers = this.subscribers.get(connectionKey);
    
    // DEBUG: Log ALL incoming messages and subscriber status
    const action = data.action || data.type || 'unknown';
    const truncatedKey = connectionKey.substring(0, 60) + '...';
    
    if (!subscribers || subscribers.size === 0) {
      console.warn(`[WebSocketManager] ⚠️ NO SUBSCRIBERS for action "${action}" on ${truncatedKey}`);
      console.warn(`[WebSocketManager] Available connectionKeys:`, Array.from(this.subscribers.keys()).map(k => k.substring(0, 60) + '...'));
      return;
    }
    
    // Check if we should buffer this message (tab in background and not a critical connection event)
    const isCriticalEvent = data.type && ['connection_opened', 'connection_closed', 'connection_error', 'connection_failed', 'ping', 'pong'].includes(data.type);
    const shouldBuffer = !this._isTabVisible && !isCriticalEvent;
    
    if (shouldBuffer) {
      // Buffer the message for processing when tab becomes visible
      if (!this.backgroundMessageBuffer.has(connectionKey)) {
        this.backgroundMessageBuffer.set(connectionKey, { messages: [], lastVisibleTime: this._lastVisibleTime });
      }
      const buffer = this.backgroundMessageBuffer.get(connectionKey);
      
      // Limit buffer size to prevent memory issues
      const MAX_BUFFER_SIZE = 100;
      if (buffer.messages.length < MAX_BUFFER_SIZE) {
        buffer.messages.push(data);
        console.log(`[WebSocketManager] 📥 Buffered message in background (${buffer.messages.length} total): ${action}`);
      } else {
        console.warn(`[WebSocketManager] ⚠️ Background buffer full, dropping message: ${action}`);
      }
      
      // Still notify subscribers using microtask - they may still process it
      // This ensures real-time updates work even in background if the browser allows
    }
    
    // Log all incoming actions to trace message flow
    if (this._isTabVisible) {
      console.log(`[WebSocketManager] 📨 Notifying ${subscribers.size} subscriber(s) of action: ${action}`);
    }
    
    // Only log important events, not every message
    if (data.type && ['connection_opened', 'connection_closed', 'connection_error', 'connection_failed'].includes(data.type)) {
      console.log(`[WebSocketManager] Notifying ${subscribers.size} subscribers: ${data.type}`);
    }
    
    // CRITICAL: Do NOT use requestAnimationFrame - it's PAUSED when tab is in background!
    // Use queueMicrotask for immediate processing even in background tabs
    queueMicrotask(() => {
      const failedSubscribers = [];
      
      subscribers.forEach((subscriber) => {
        try {
          subscriber.handler(data);
        } catch (error) {
          console.error(`[WebSocketManager] Error in subscriber handler for ${subscriber.componentId}:`, error);
          // Store the actual subscriber object reference for deletion
          failedSubscribers.push(subscriber);
        }
      });
      
      // Remove failed handlers using the actual object reference
      failedSubscribers.forEach((subscriber) => {
        subscribers.delete(subscriber);
        console.warn(`[WebSocketManager] Removed failed subscriber: ${subscriber.componentId}`);
      });
    });
  }

  /**
   * Clean up a connection
   */
  cleanupConnection(connectionKey) {
    console.log(`[WebSocketManager] Cleaning up connection: ${connectionKey}`);
    
    const connection = this.connections.get(connectionKey);
    if (connection) {
      // Clear Railway proactive reconnect timer
      /*
      if (connection.railwayReconnectTimer) {
        clearTimeout(connection.railwayReconnectTimer);
        connection.railwayReconnectTimer = null;
      }
      */

      // Destroy keepalive
      if (connection.keepalive) {
        connection.keepalive.destroy();
      }
      
      // Close WebSocket
      if (connection.ws && connection.ws.readyState !== WebSocket.CLOSED) {
        connection.ws.close(1000, 'Cleanup');
      }
      
      // Clear from maps
      this.connections.delete(connectionKey);
      this.subscribers.delete(connectionKey);
      this.messageQueue.delete(connectionKey);
      this.backgroundMessageBuffer.delete(connectionKey);
    }
  }

  /**
   * Get connection status
   */
  getConnectionStatus(endpoint, token) {
    const { connectionKey } = this._buildUrlAndKey(endpoint, token);
    const connection = this.connections.get(connectionKey);
    
    if (!connection) {
      return 'disconnected';
    }
    
    switch (connection.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return connection.isReady ? 'connected' : 'authenticating';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'disconnected';
      default:
        return 'unknown';
    }
  }

  /**
   * Get raw connection info (for monitoring/refresh heuristics)
   */
  getConnectionInfo(endpoint, token) {
    const { connectionKey } = this._buildUrlAndKey(endpoint, token);
    return this.connections.get(connectionKey) || null;
  }

  /**
   * Force reconnection for a specific endpoint
   */
  forceReconnect(endpoint, token) {
    const { connectionKey } = this._buildUrlAndKey(endpoint, token);
    const connection = this.connections.get(connectionKey);
    
    if (connection && connection.keepalive) {
      connection.keepalive.forceReconnect();
    }
  }

  /**
   * Manually flush the message queue for a connection
   */
  flushMessageQueue(endpoint, token) {
    const { connectionKey } = this._buildUrlAndKey(endpoint, token);
    console.log(`[WebSocketManager] Manually flushing queue for ${connectionKey}`);
    this.processQueuedMessages(connectionKey);
  }

  /**
   * Force refresh all connections with a new token
   * Called when token is refreshed by AuthContext
   */
  refreshAllConnectionsWithNewToken(newToken) {
    console.log('[WebSocketManager] Refreshing all connections with new token');
    this.connections.forEach((connection, oldKey) => {
      try {
        const url = new URL(oldKey);
        const oldToken = url.searchParams.get('token');
        if (oldToken === newToken) return;

        const endpoint = connection.endpoint;
        const subs = this.subscribers.get(oldKey);
        const subscribersList = subs ? Array.from(subs) : [];

        // Update keepalive token so reconnects use the new token
        if (connection.keepalive && typeof connection.keepalive.updateToken === 'function') {
          connection.keepalive.updateToken(newToken);
        }

        // Cleanup old connection
        this.cleanupConnection(oldKey);

        // Create new connection and reattach subscribers
        this.getConnection(endpoint, newToken).then(() => {
          const { connectionKey: newKey } = this._buildUrlAndKey(endpoint, newToken);
          const newSubs = this.subscribers.get(newKey) || new Set();
          subscribersList.forEach((s) => newSubs.add(s));
          this.subscribers.set(newKey, newSubs);
        }).catch(() => {});
      } catch (e) {
        // ignore
      }
    });
  }

  /**
   * Clean up all connections
   */
  cleanup() {
    console.log('[WebSocketManager] Cleaning up all connections');
    
    // Clear periodic cleanup
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    // Remove lifecycle listeners
    if (typeof window !== 'undefined') {
      try {
        if (this._handleBeforeUnload) {
          window.removeEventListener('beforeunload', this._handleBeforeUnload, { once: true });
        }
        if (this._handleVisibilityChange) {
          document.removeEventListener('visibilitychange', this._handleVisibilityChange);
        }
        // Clean up PWA-specific event listeners
        if (this._handlePageShow) {
          window.removeEventListener('pageshow', this._handlePageShow);
        }
        if (this._handleFocus) {
          window.removeEventListener('focus', this._handleFocus);
        }
        // Clean up network event listeners
        if (this._handleOnline) {
          window.removeEventListener('online', this._handleOnline);
        }
        if (this._handleOffline) {
          window.removeEventListener('offline', this._handleOffline);
        }
      } catch (e) {
        // noop
      }
    }
    
    this.connections.forEach((connection, connectionKey) => {
      this.cleanupConnection(connectionKey);
    });
    
    this.connections.clear();
    this.subscribers.clear();
    this.messageQueue.clear();
    this.backgroundMessageBuffer.clear();
  }

  /**
   * Clean up stale connections for the same endpoint but different token
   * This prevents duplicate backend consumers when token is refreshed
   */
  _cleanupStaleConnectionsForEndpoint(endpoint, currentConnectionKey) {
    try {
      // Extract base endpoint (without query params) for comparison
      const currentUrl = new URL(endpoint, typeof window !== 'undefined' ? window.location.href : undefined);
      const baseEndpoint = `${currentUrl.origin}${currentUrl.pathname}`;
      
      // Find and cleanup connections with same base endpoint but different key
      const keysToCleanup = [];
      for (const [existingKey, connection] of this.connections.entries()) {
        if (existingKey === currentConnectionKey) continue; // Skip current connection
        
        try {
          // Check if this connection is for the same base endpoint
          const existingUrl = new URL(existingKey);
          const existingBase = `${existingUrl.origin}${existingUrl.pathname}`;
          
          if (existingBase === baseEndpoint) {
            console.log(`[WebSocketManager] 🧹 Found stale connection with different token for ${baseEndpoint}, cleaning up`);
            keysToCleanup.push(existingKey);
          }
        } catch (e) {
          // Skip malformed keys
        }
      }
      
      // Cleanup stale connections immediately
      for (const key of keysToCleanup) {
        this.cleanupConnection(key);
      }
      
      if (keysToCleanup.length > 0) {
        console.log(`[WebSocketManager] 🧹 Cleaned up ${keysToCleanup.length} stale connection(s) for ${baseEndpoint}`);
      }
    } catch (e) {
      console.warn('[WebSocketManager] Error cleaning up stale connections:', e);
    }
  }

  /**
   * Build a canonical WebSocket URL and stable connection key
   */
  _buildUrlAndKey(endpoint, token) {
    try {
      // If endpoint is already an absolute ws(s) URL, use it directly
      const url = new URL(endpoint, typeof window !== 'undefined' ? window.location.href : undefined);
      // Ensure token query param is present and up to date
      if (token) {
        url.searchParams.set('token', token);
      }
      // Create a stable key by sorting params
      const sortedParams = Array.from(url.searchParams.entries())
        // Ignore cache-busting params in the connection key
        .filter(([k]) => k !== 'r')
        .sort(([a], [b]) => (a > b ? 1 : a < b ? -1 : 0))
        .map(([k, v]) => `${k}=${v}`)
        .join('&');
      const connectionKey = `${url.origin}${url.pathname}${sortedParams ? '?' + sortedParams : ''}`;
      return { fullUrl: url.toString(), connectionKey };
    } catch (e) {
      // Fallback: avoid double '?'
      const hasQuery = endpoint.includes('?');
      const sep = hasQuery ? '&' : '?';
      const fullUrl = token ? `${endpoint}${sep}token=${token}` : endpoint;
      return { fullUrl, connectionKey: fullUrl };
    }
  }
}

// Create singleton instance
const webSocketManager = new WebSocketManager();

export default webSocketManager; 